import React, {useContext, useEffect, useState} from "react";
import {Button, Intent} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import styled from "@emotion/styled";
import {useOktaAuth} from "@okta/okta-react";
import log from "loglevel";
import {useAppContext} from "../../../ApplicationContext";
import {COLORS, TenantPreferences} from "../../../constants/constants";
import {Job} from "../../../generated/graphql";
import {AuthContext} from "../../../providers/AuthProvider";
import {extractSimplePref, usePreferenceContext} from "../../../providers/PreferenceProvider";
import {navigateToCmsOrder} from "../../../services/CmsService";
import {
  formatDate,
  getLocalDate,
  getLocalTime,
  getTimeAtSpecificTimeZone,
  UnknownString
} from "../../../utils/DateTimeUtils";
import {deliveryStop, pickupStop} from "../../../utils/stops/StopUtils";
import {DoubleFieldCellValue, StackedFieldCellRenderer} from "../../common/cell-renderers/DoubleFieldCellRenderer";
import {ServiceBadge} from "../../common/ServiceBadge";
import VehicleIconResolver from "../../vehicles/VehicleIconResolver";
import {Empty, Table, TableFrame} from "./CsvImportStopsTable";
import {CsvImportCreatedOrder} from "./types";

export const CSV_IMPORT_JOBS_PAGE_SIZE = 50;

type CsvImportJobsTableProps = {
  orders: CsvImportCreatedOrder[];
  testId?: string;
};

// Columns, names and values of the Unassigned Jobs grid on Step 1 (JobsGrid), one row per created job
const COLUMNS = [
  "Job #",
  "Order Auth",
  "Ready At",
  "Due By",
  "Customer Account",
  "Caller",
  "Pickup Zone",
  "Delivery Zone",
  "SVC Type",
  "Vehicle",
  "Pickup Address",
  "Delivery Address",
  "Pieces",
  "Weight",
  "Order Notes",
  "Pickup Notes",
  "Delivery Notes"
];

// Blank includes whitespace: an import without a Caller sends a single space (CMS would record "OrderLoad")
const text = (value: UnknownString | number) =>
  value === undefined || value === null || String(value).trim() === "" ? <Empty>—</Empty> : String(value);

/** The created jobs as the Unassigned Jobs grid shows them, 50 per page; Job # opens the order in Order Entry. */
const CsvImportJobsTable = ({orders, testId}: CsvImportJobsTableProps) => {
  const authState = useContext(AuthContext);
  const {oktaAuth} = useOktaAuth();
  const {appState} = useAppContext();
  const {tenantPreferences} = usePreferenceContext();
  const timezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false).value;
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(orders.length / CSV_IMPORT_JOBS_PAGE_SIZE));

  // A new result starts on the first page
  useEffect(() => setPage(0), [orders]);

  // Ready At / Due By as the grid formats them: time (with zone) over date, in the stop's time zone when the
  // tenant preference says so, else the browser's
  const dateCell = (dateStr: UnknownString, timeZone: UnknownString): DoubleFieldCellValue => {
    const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
    const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;
    return {
      upperField: {
        value:
          getTimeFnc({dateStr, timeZone, isTimeFormat24hr: appState.isTimeFormat24hr, options: {includeZone: true}}) ??
          ""
      },
      lowerField: {value: getDateFnc(dateStr, timeZone, "EEE MMM dd yyyy") ?? ""}
    };
  };

  const openOrder = (job: Job) => {
    if (!authState.tenant) return;
    navigateToCmsOrder(authState.tenant, oktaAuth.getAccessToken() ?? "", job.order?.orderId, tenantPreferences).catch(
      (error) => log.error("Failed to open the order from the file import", error)
    );
  };

  const first = page * CSV_IMPORT_JOBS_PAGE_SIZE;
  const pageOrders = orders.slice(first, first + CSV_IMPORT_JOBS_PAGE_SIZE);

  return (
    <div data-testid={testId}>
      <TableFrame>
        <JobsTable>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageOrders.map(({job, createdOrderNumber}) => {
              const pickup = pickupStop(job.stops ?? []);
              const delivery = deliveryStop(job.stops ?? []);
              return (
                <tr key={createdOrderNumber}>
                  <td>
                    <Button
                      variant="minimal"
                      intent={Intent.PRIMARY}
                      text={job.jobNumber || createdOrderNumber}
                      onClick={() => openOrder(job)}
                      style={{fontSize: "12px", padding: "2px"}}
                      data-testid={`csv-import-job-${job.jobNumber || createdOrderNumber}`}
                    />
                  </td>
                  <td>{text(job.order?.auth)}</td>
                  <td>{StackedFieldCellRenderer(dateCell(job.readyAt, pickup?.timeZone))}</td>
                  <td>{StackedFieldCellRenderer(dateCell(job.dueBy, delivery?.timeZone))}</td>
                  <td>{text(job.order?.customer?.name)}</td>
                  <td>{text(job.order?.caller)}</td>
                  <td>{text(pickup?.dispatchZone)}</td>
                  <td>{text(delivery?.dispatchZone)}</td>
                  <td>{job.service ? <ServiceBadge service={job.service} ellipsize /> : <Empty>—</Empty>}</td>
                  <td>
                    {job.vehicleType?.description ? (
                      <FontAwesomeIcon
                        icon={VehicleIconResolver(job.vehicleType.description, tenantPreferences)}
                        title={job.vehicleType.description}
                        color={COLORS.TEXT_GRAY_MEDIUM}
                        size="lg"
                        data-testid="csv-import-vehicle-icon"
                      />
                    ) : (
                      <Empty>—</Empty>
                    )}
                  </td>
                  <td>{text(pickup?.address)}</td>
                  <td>{text(delivery?.address)}</td>
                  <td>{text(job.pieces)}</td>
                  <td>{text(job.weight)}</td>
                  <td>{text(job.order?.notes)}</td>
                  <td>{text(pickup?.note)}</td>
                  <td>{text(delivery?.note)}</td>
                </tr>
              );
            })}
          </tbody>
        </JobsTable>
      </TableFrame>
      {orders.length > CSV_IMPORT_JOBS_PAGE_SIZE && (
        <Pager data-testid="csv-import-jobs-pager">
          {/* Announced on every page change for screen reader users */}
          <span aria-live="polite">
            {(first + 1).toLocaleString("en-US")}–
            {Math.min(first + pageOrders.length, orders.length).toLocaleString("en-US")} of{" "}
            {orders.length.toLocaleString("en-US")} jobs
          </span>
          {/* aria-disabled, not disabled: a disabled button under focus would drop keyboard focus to the body */}
          <PagerButton
            variant="minimal"
            icon="chevron-left"
            aria-label="Previous page"
            aria-disabled={page === 0}
            onClick={() => page > 0 && setPage(page - 1)}
          />
          <span>
            Page {page + 1} of {pageCount}
          </span>
          <PagerButton
            variant="minimal"
            icon="chevron-right"
            aria-label="Next page"
            aria-disabled={page >= pageCount - 1}
            onClick={() => page < pageCount - 1 && setPage(page + 1)}
          />
        </Pager>
      )}
    </div>
  );
};

const JobsTable = styled(Table)`
  td {
    vertical-align: middle;
  }
`;

const PagerButton = styled(Button)`
  &[aria-disabled="true"] {
    opacity: 0.5;
    cursor: default;
  }
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 4px 0;
  font-size: 13px;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
`;

export default CsvImportJobsTable;
